import { Button } from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import React,{ useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { TinyUser, User } from "../../../../types/user";
import { AccessPermissionEnum } from "../../../../enums/accessPermissionEnum";
import AddUserDialog from "./AddUserDialog";
import useLocale from "../../../../hooks/useLocale";
import { Dictionary } from "../../../../types/Dictionary";
import usersService from "../../../../services/users.service";
import useAuth from "../../../../hooks/useAuth";
import NotFoundError from "../../../error/NotFoundError";
import { Tooltip } from "@progress/kendo-react-tooltip";
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from "@progress/kendo-react-layout";
import { Switch, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import UnauthorizedAccess from "../../../error/UnauthorizedAccess";
import useSwal from "../../../../hooks/useSwal";
import useMasterData from "../../../../hooks/useMasterData";
import CardWordFilter from "./CardWordFilter";
import useTranslation from "../../../../hooks/useTranslation";
import { Page } from "../../../../types/Page";
import UserListPaginated from "./UserListPaginated";
import CustomSearchFieldTextInput from "../../../../components/custom/form/CustomSearchFieldTextInput";
import CustomUserGridInfoLocalizationProp from "../../../../components/custom/grid/CustomUserGridInfoLocalizationProp";

const apLocaleKeyName: { [key: string]: string } = {
  "Super Admin": "SuperAdmin",
  "Recording Admin": "RecordingAdmin",
  "Playback Supervisor": "PlaybackSupervisor",
  "Recording Agent": "RecordingAgent",
};

const initialDataState = {
  skip: 0,
  take: 50,
  totalRecords: 0,
  sort: {
    sortField: "email",
    sortDirection: "asc"
  }
};

interface CustomTabTitleProp {
  name: string;
  count?: number;
}

interface CustomTabStripTabProps {
  name: string;
}

export interface eCustomUserCardDetailProps {
  user: User;
  index:number;
}

interface UserManagementProps {}

const CustomTabTitle = (props: CustomTabTitleProp) => {
  return (
    <span>
      <span>{props.name}</span>
      {props.count!==undefined && <span className="tabBadge m-l-2">{props.count}</span>}
    </span>
  );
};

const UserManagement: React.FC<UserManagementProps> = () => {
  const trans = useTranslation("UserManagement");
  const master = useMasterData();
  const auth = useAuth();
  const Swal = useSwal();
  const navigate = useNavigate();
  const localeCtx = useLocale();
  const location = useLocation();
  // const backActive = location?.state?.backActive === true;
  // const [localStorageFilter, setLocalStorageFilter] =
  //   useLocalStorage<CardsPageFilter>(
  //     "localStorageUserFilterData",
  //     {...initialDataState,searchTerm:"",selectedTab:0}
  //   );
  const [users, setUsers] = useState<User[]>([]);
  // const [page, setPage] = useState<Page>(backActive ? {skip:localStorageFilter.skip,take:localStorageFilter.take,totalRecords:localStorageFilter.totalRecords,sort:localStorageFilter.sort} : initialDataState);
  const [page, setPage] = useState<Page>(initialDataState);
  const [error, setError] = useState<string | undefined>();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedTabName, setSelectedTabName] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [addUserDialogVisible, setAddUserDialogVisible] =
    useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User>();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalUsersCount,setTotalUsersCount]=useState<number>(0);
  const [temporaryUserIds,setTemporaryUserIds]=useState<number[]>([]);

  useEffect(() => {
    if (!localeCtx?.selectedLocale?.current.componentTranslations["UserManagement"]) {
      trans.fetchTranslations("UserManagement");
    }
  }, [localeCtx?.selectedLocale]);

  useEffect(() => {
    getCurrentUser();
  }, []);

  useEffect(()=>{
    fetchFirstSetOfUsers("");
  },[])

  const fetchFirstSetOfUsers = async (tabName:string) => {
    setLoading(true);
    setError("");
    setTotalUsersCount(0);
    try {
      const allUsersPaginatedData = await usersService.fetchAllUsersPaginated(tabName,initialDataState.skip,initialDataState.take,searchTerm,initialDataState.sort?.sortField,initialDataState.sort?.sortDirection);
      if(allUsersPaginatedData.data){
        setUsers(prev=>[...prev,...allUsersPaginatedData.data ?? []])
        setPage({...allUsersPaginatedData.page,skip: (allUsersPaginatedData.page.skip ?? 0) + (allUsersPaginatedData.page.take ?? 0), take:initialDataState.take});
        setTotalUsersCount(allUsersPaginatedData.page.totalRecords ?? 0);
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const sendRequest = async () => {
    setError("");
    try {
      const allUsersPaginatedData = await usersService.fetchAllUsersPaginated(selectedTabName,page.skip,page.take,searchTerm,page.sort?.sortField,page.sort?.sortDirection);
      if(allUsersPaginatedData.data){
        if(temporaryUserIds.length>0){
          for(let i=0;i<temporaryUserIds.length;i++){
            let tUserId = temporaryUserIds[i];
            let findtemporaryUserIdInRequestedData = allUsersPaginatedData.data.find((item:User)=>item.id===tUserId);
            if(findtemporaryUserIdInRequestedData){
              setUsers(prev=>[...prev.filter((item:User)=>item.id!==tUserId),...allUsersPaginatedData.data ?? []])
              setTemporaryUserIds(temporaryUserIds.filter((id:number)=>id!==tUserId));
            }else{
              setUsers(prev=>[...prev,...allUsersPaginatedData.data ?? []])
            }
          }
        }else{
          setUsers(prev=>[...prev,...allUsersPaginatedData.data ?? []])
        }
        setPage({...allUsersPaginatedData.page,skip: (allUsersPaginatedData.page.skip ?? 0) + (allUsersPaginatedData.page.take ?? 0),take:initialDataState.take})
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      }
    }
  };

  const getCurrentUser = async () => {
    try {
      const user = await usersService.getCurrentUser();
      setCurrentUser(user);
    } catch (ex) {
      if (ex instanceof Error) {
        console.log(ex);
      }
    }
  };

  const handleTabSelection = (e: TabStripSelectEventArguments) => {
    setUsers([]);
    setPage(initialDataState);
    setSelectedTab(e.selected);
    switch(e.selected){
      case 0:
        setSelectedTabName("");
        fetchFirstSetOfUsers("");
        break;
      case 1:
        setSelectedTabName("Super Admin");
        fetchFirstSetOfUsers("Super Admin");
        break;
      case 2:
        setSelectedTabName("Recording Admin");
        fetchFirstSetOfUsers("Recording Admin");
        break;
      case 3:
        setSelectedTabName("Playback Supervisor");
        fetchFirstSetOfUsers("Playback Supervisor");
        break;
      case 4:
        setSelectedTabName("Recording Agent");
        fetchFirstSetOfUsers("Recording Agent");
        break;
      case 5:
        setSelectedTabName("Other");
        fetchFirstSetOfUsers("Other");
        break;
    }
  };

  // const handleFilterData = () => {
  //   const data: CardsPageFilter = {
  //     searchTerm: searchTerm,
  //     selectedTab: selectedTab,
  //     skip: page.skip ?? 0,
  //     take: page.take ?? 0,
  //     totalRecords: page.totalRecords ?? 0,
  //     sort: {
  //       sortField: page.sort?.sortField ?? "",
  //       sortDirection: page.sort?.sortDirection ?? ""
  //     }
  //   };
  //   setLocalStorageFilter(data);
  // };

  const handleSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSearchTerm(e.currentTarget.value);
  };

  const handleSearchTextOnKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      setPage(initialDataState);
      setUsers([]);
      fetchFirstSetOfUsers(selectedTabName);
    }
  };

  const toggleAddUserDialog = () => {
    setAddUserDialogVisible(!addUserDialogVisible);
  };

  const addNewUser = async (newUser: User) => {
    await getCurrentUser();
    const updatedUsers = [newUser,...users];
    setTemporaryUserIds([...temporaryUserIds,newUser.id]);
    setUsers(updatedUsers);
    master?.fetchUpdatedUsers();
  };

  const deleteUser = (user: User) => {
    if (user.id === auth?.user?.id) {
      return;
    }
    Swal.fire({
      title: trans.fetchLabelKeyTranslation(
        "SwtAltChangeTitle",
        "Are you sure?"
      ),
      text: trans.fetchLabelKeyTranslation(
        "SwtAltDeleteUserText",
        "You want to delete this User."
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: trans.fetchLabelKeyTranslation(
        "ContinueButton",
        "Confirm"
      ),
      cancelButtonText: trans.fetchLabelKeyTranslation(
        "CancelButton",
        "Cancel"
      ),
    })
    .then(async (result) => {
      if (result.isConfirmed) {
        await usersService
          .deleteUser(user.id)
          .then(async () => {
            setTotalUsersCount(totalUsersCount-1);
            setUsers([...users.filter((ele) => ele.id !== user.id)]);
            master?.fetchUpdatedUsers();
            Swal.fire({
              icon: "success",
              title: trans.fetchLabelKeyTranslation(
                "SwtAltUserAdded",
                "User Deleted"
              ),
              confirmButtonText: trans.fetchLabelKeyTranslation("OKText", "OK"),
            });
          })
          .catch((err) => {
            if (err instanceof Error) {
              Swal.fire({
                icon: "error",
                title: trans.fetchLabelKeyTranslation("SwtAltTitle", "Oops…"),
                text: trans.fetchLabelKeyTranslation(
                  "SwtAltWrong",
                  "Something went wrong! Please Try again…"
                ),
                confirmButtonText: trans.fetchLabelKeyTranslation(
                  "OKText",
                  "OK"
                ),
              });
            }
          });
      }
    });
  };

  const updateUserLogin = async (user: User, loginEnabled: boolean) => {
    await usersService
      .updateUserLogin(user.id, loginEnabled)
      .then(async () => {
        let _users = users.map((u) => {
          if (u.id === user.id) {
            return { ...u, loginEnabled: loginEnabled };
          } else {
            return { ...u };
          }
        });
        setUsers(_users);
        Swal.fire({
          icon: "success",
          title: trans.fetchLabelKeyTranslation(
            "UserLoginSwitch",
            "User Login Updated"
          ),
          confirmButtonText: trans.fetchLabelKeyTranslation("OKText", "OK"),
        });
      })
      .catch((err) => {
        if (err instanceof Error) {
          Swal.fire({
            icon: "error",
            title: trans.fetchLabelKeyTranslation("SwtAltTitle", "Oops…"),
            text: trans.fetchLabelKeyTranslation(
              "SwtAltWrong",
              "Something went wrong! Please Try again…"
            ),
            confirmButtonText: trans.fetchLabelKeyTranslation("OKText", "OK"),
          });
        }
      });
  };

  const GetFilteredUsers = (profileName: string) => {
    let _filteredUsers = users
      ?.filter(
        (u) =>
          (profileName === "All" ||
            (profileName === "Other"
              ? u.authorizationProfile?.isSystemDefined === false
              : u.authorizationProfile?.name === profileName))
      )
    return _filteredUsers.filter((u) => !u.isArchived);
  };

  const CustomUserCardDetail = (
    userCardDetailProps: eCustomUserCardDetailProps
  ) => {
    const user = userCardDetailProps.user;
    const [loginLoading, setLoginLoading] = useState<boolean>(false);
    const _userName = `${user.firstName ? user.firstName : ""} ${
      user.lastName ? user.lastName : ""
    }`.trim();
    let idValue;
    if (_userName !== "" && _userName.length > 0) {
      idValue = "userCardId_" + _userName.charAt(0).toLocaleLowerCase();
    }
    const handleLoginClick = async (event: SwitchChangeEvent) => {
      setLoginLoading(true);      
      await updateUserLogin(user, event.value);
      setLoginLoading(false);
    };

    const navigateToProfile = () => {
      // handleFilterData();
      navigate(`user/${user.id}`);
    };

    return (
      <div
        className="col col-xl-3 col-lg-4 col-md-6 col-sm-12"
        key={user.id}
        id={idValue}
      >
        <div className="cardListBlock">
          <div className="cardRows">
            <div className="cardColumn">
              <div className="colUser">
                 <CustomUserGridInfoLocalizationProp
                  userInfo={{
                    id: user.id,
                    loginUserId: user.loginUserId,
                    firstName: user.firstName ? user.firstName : "",
                    lastName: user.lastName ? user.lastName : "",
                    image: user.image ? user.image : "",
                    email: user.email,
                    phoneNo: "",
                    isArchived: user.isArchived,
                  }}
                  navigateToProfile={{
                    navigate: true,
                    navigateFunction: navigateToProfile,
                  }}
                  iconCls="bi bi-person"
                />
              </div>
              <div className="lblGroup float-left w-100">
                <div
                  onClick={navigateToProfile}
                  className="fs-12 font-weight-semi p-t-7 p-b-7 text-truncate cursor-pointer"
                >
                  <span>
                    {user.authorizationProfile?.name !== undefined
                      ? trans.fetchLabelKeyTranslation(
                          apLocaleKeyName[user.authorizationProfile?.name] ??
                            "",
                          user.authorizationProfile?.name
                        )
                      : ""}
                  </span>
                </div>
                {user?.devices != null &&
                 user.devices.length > 0 &&
                  <div
                  onClick={navigateToProfile}
                  className="fs-12 font-weight-semi p-t-7 p-b-7 text-truncate cursor-pointer"
                >
                  <span className="float-left text-black-9 p-b-2">
                    {trans.fetchLabelKeyTranslation(
                      "UserManagementColumnLicense",
                      "Licence"
                    )}
                    : &nbsp;
                  </span>
                  <span>
                    {user.licensePackage
                      ? master?.data?.licensePackages.find(
                          (x) => x.id === user?.licensePackage?.id
                        )?.localizationValue
                      : trans.fetchLabelKeyTranslation("NoneText", "None")}
                  </span>
                </div>}
                <div
                  onClick={navigateToProfile}
                  className="minTextLabel p-t-1 cursor-pointer"
                >
                  <span
                    className="float-left text-black-9 p-b-2"
                    style={{ minWidth: "77px" }}
                  >
                    {trans.fetchLabelKeyTranslation(
                      "UserManagementColumnRecordedDevice",
                      "Recorded Device"
                    )}
                    :
                  </span>
                  <span className="p-l-3">
                    <span className="badgeList">
                      <div className="keywordRow">
                        {user.devices.length > 0 ? (
                          user.devices.map((device:any) => {
                            return (
                              <div
                                className="keywordTrk"
                                key={device.deviceIdentifier}
                              >
                                {device.deviceIdentifier}
                              </div>
                            );
                          })
                        ) : (
                          <div className="keywordTrk">
                            {trans.fetchLabelKeyTranslation("TextNone", "None")}
                          </div>
                        )}
                      </div>
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="blockIconButton">
            {currentUser?.recordingNetwork?.find(
              (u: TinyUser) => u.id === userCardDetailProps.user.id
            ) &&
            auth?.checkUserAccess(AccessPermissionEnum.ManageUsersAndGroups) ? (
              <>
                <span className="btnBlocks">
                  <div
                    className="switchButton"
                    title={
                      user.loginEnabled
                        ? trans.fetchLabelKeyTranslation(
                            "LoginEnabledText",
                            "Login Enabled"
                          )
                        : trans.fetchLabelKeyTranslation(
                            "LoginDisabledText",
                            "Login Disabled"
                          )
                    }
                  >
                    {loginLoading ? (
                      <Loader />
                    ) : (
                      <Switch
                        checked={user.loginEnabled}
                        onChange={(event) => handleLoginClick(event)}
                        disabled={
                          master?.telepoDisableConfig?.usermanagement.users
                            .profile.disableEnableLoginToggle
                        }
                      />
                    )}
                  </div>
                </span>
                <span className="btnBlocks">
                  <a
                    className="btnBlock-a cursor-pointer"
                    onClick={navigateToProfile}
                    title={trans.fetchLabelKeyTranslation(
                      "EditButtonText",
                      "Edit"
                    )}
                  >
                    <i className={`bi bi-pencil fs-17`}></i>
                  </a>
                </span>
                {user.id !== auth?.user?.id && (
                  <span className="btnBlocks">
                    <a
                      style={
                        master?.telepoDisableConfig?.usermanagement.users
                          .disableDeleteUserButton
                          ? { pointerEvents: "none" }
                          : {}
                      }
                      className={`btnBlock-a cursor-pointer ${
                        master?.telepoDisableConfig?.usermanagement.users
                          .disableDeleteUserButton
                          ? "disabledIconBtn"
                          : ""
                      }`}
                      title={trans.fetchLabelKeyTranslation(
                        "DeleteButtonText",
                        "Delete"
                      )}
                      onClick={() => deleteUser(user)}
                    >
                      <i className="bi bi-trash3 fs-17"></i>
                    </a>
                  </span>
                )}
              </>
            ) : (
              <span className="btnBlocks">
                 <a
                  onClick={navigateToProfile}
                  className="btnBlock-a cursor-pointer"
                  title={trans.fetchLabelKeyTranslation(
                    "UserNotFoundMsg",
                    "View User Profile"
                  )}
                >
                  <i className="bi bi-eye fs-17"></i>
                </a>
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {!auth?.checkUserAccess(AccessPermissionEnum.ManageUsersAndGroups) ? (
        <UnauthorizedAccess />
      ) : (
        <Tooltip anchorElement={"target"} parentTitle={true} position={"top"}>
          <div className="contentPushArea boxUsers h-100">
            <div className="contentHead h-100" style={{position:'relative'}}>
              <div className="contentHeadTop d-flex justify-content-between align-items-center">
                <div className="contentHeadTitle fs-18">
                  {trans.fetchLabelKeyTranslation(
                    "UserManagementTitle",
                    "Users"
                  )}
                </div>
                <div className="contentHeadAction">
                  <div className="buttons-container hov-transparent d-flex align-itmes-center p-0">
                    <div className="searchCol searchBox m-r-10">
                      <CustomSearchFieldTextInput
                        className="input-search"
                        placeholder={trans.fetchLabelKeyTranslation(
                          "UserManagementSearchBoxPlaceHolder",
                          "Search..."
                        )}
                        value={searchTerm}
                        updateValue={setSearchTerm}
                        searchTextChangeHandler={handleSearchChange}
                        handleOnKeyDown={handleSearchTextOnKeyDown}
                        onEscapeKeyFunc={false}
                        disabled={loading}
                      />
                    </div>
                    <Button
                      onClick={toggleAddUserDialog}
                      className={`btn bg-primary text-white fs-13 fw-normal ${
                        !auth?.checkUserAccess(
                          AccessPermissionEnum.ManageUsersAndGroups
                        ) ||
                        master?.telepoDisableConfig?.usermanagement.users
                          .disableAddUserButton
                          ? "disabledBtn"
                          : ""
                      }`}
                      style={{ height: "29px", margin: "-1px 0 0 0" }}
                      disabled={
                        !auth?.checkUserAccess(
                          AccessPermissionEnum.ManageUsersAndGroups
                        ) ||
                        master?.telepoDisableConfig?.usermanagement.users
                          .disableAddUserButton
                      }
                      title={trans.fetchLabelKeyTranslation(
                        "UserManagementAddButton",
                        "Add"
                      )}
                    >
                      {trans.fetchLabelKeyTranslation(
                        "UserManagementAddButton",
                        "Add"
                      )}
                    </Button>
                    {addUserDialogVisible && (
                      <AddUserDialog
                        toggleDialog={toggleAddUserDialog}
                        addNewUser={addNewUser}
                        currentUser={currentUser}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="contentHeadTab h-100" style={{position:'relative'}}>
                <div className="tabLabel tabLeftPadding-0 h-100">
                  <TabStrip
                    selected={selectedTab}
                    onSelect={handleTabSelection}
                  >
                    <TabStripTab
                      title={
                        <CustomTabTitle
                          name={trans.fetchLabelKeyTranslation(
                            "AllTabTxt",
                            "All"
                          )}
                           count={selectedTab===0 ? totalUsersCount : undefined}
                        />
                      }
                    >
                      <UserListPaginated GetFilteredUsers={GetFilteredUsers} CustomUserCardDetail={CustomUserCardDetail} page={page} sendRequest={sendRequest} tabStripName={"All"} loading={loading} error={error ?? ""}/>
                    </TabStripTab>
                    <TabStripTab
                      title={
                        <CustomTabTitle
                          name={trans.fetchLabelKeyTranslation(
                            "SuperAdmin",
                            "Super Admin"
                          )}
                          count={selectedTab===1 ? totalUsersCount : undefined}
                        />
                      }
                    >
                      <UserListPaginated GetFilteredUsers={GetFilteredUsers} CustomUserCardDetail={CustomUserCardDetail} page={page} sendRequest={sendRequest} tabStripName={"Super Admin"} loading={loading} error={error ?? ""}/>
                    </TabStripTab>
                    <TabStripTab
                      title={
                        <CustomTabTitle
                          name={trans.fetchLabelKeyTranslation(
                            "RecordingAdmin",
                            "Recording Admin"
                          )}
                           count={selectedTab===2 ? totalUsersCount : undefined}
                        />
                      }
                    >
                      <UserListPaginated GetFilteredUsers={GetFilteredUsers} CustomUserCardDetail={CustomUserCardDetail} page={page} sendRequest={sendRequest} tabStripName={"Recording Admin"} loading={loading} error={error ?? ""}/>
                    </TabStripTab>
                    <TabStripTab
                      title={
                        <CustomTabTitle
                          name={trans.fetchLabelKeyTranslation(
                            "PlaybackSupervisor",
                            "Playback Supervisor"
                          )}
                          count={selectedTab===3 ? totalUsersCount : undefined}
                        />
                      }
                    >
                      <UserListPaginated GetFilteredUsers={GetFilteredUsers} CustomUserCardDetail={CustomUserCardDetail} page={page} sendRequest={sendRequest} tabStripName={"Playback Supervisor"} loading={loading} error={error ?? ""}/>
                    </TabStripTab>
                    <TabStripTab
                      title={
                        <CustomTabTitle
                          name={trans.fetchLabelKeyTranslation(
                            "RecordingAgent",
                            "Recording Agent"
                          )}
                          count={selectedTab===4 ? totalUsersCount : undefined}
                        />
                      }
                    >
                      <UserListPaginated GetFilteredUsers={GetFilteredUsers} CustomUserCardDetail={CustomUserCardDetail} page={page} sendRequest={sendRequest} tabStripName={"Recording Agent"} loading={loading} error={error ?? ""}/>
                    </TabStripTab>
                    <TabStripTab
                      title={
                        <CustomTabTitle
                          name={trans.fetchLabelKeyTranslation(
                            "OtherTabTxt",
                            "Other"
                          )}
                          count={selectedTab===5 ? totalUsersCount : undefined}
                        />
                      }
                    >
                      <UserListPaginated GetFilteredUsers={GetFilteredUsers} CustomUserCardDetail={CustomUserCardDetail} page={page} sendRequest={sendRequest} tabStripName={"Other"} loading={loading} error={error ?? ""}/>
                    </TabStripTab>
                  </TabStrip>
                </div>
                <CardWordFilter
                  selectedAlphabet={"A"}
                  handleAlphabetFilterChange={()=>{}}
                  fetchLabelKeyTranslation={trans.fetchLabelKeyTranslation}
                ></CardWordFilter>
              </div>
            </div>
          </div>
        </Tooltip>
      )}
    </>
  );
};

export default UserManagement;
